import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FiHome, FiSearch, FiSettings, FiBookmark, FiChevronDown, FiChevronUp, FiUser } from 'react-icons/fi';
import { ReactComponent as StarIcon } from '../assets/star.svg';

const SidebarMenuItem = ({ item, hideIcon = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasSubmenu = item.submenu && item.submenu.length > 0;

  const toggleSubmenu = () => {
    if (hasSubmenu) {
      setIsOpen(!isOpen);
    }
  };

  const MainLink = ({ children }) => 
    hasSubmenu ? (
      <div
        onClick={toggleSubmenu}
        className="flex items-center justify-between gap-4 w-full px-4 py-3 cursor-pointer text-brand-light-gray hover:bg-brand-gray rounded-lg transition-colors mx-2"
      >
        {children}
      </div>
    ) : (
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          `flex items-center gap-4 w-full px-4 py-3 rounded-lg transition-colors duration-300 mx-2 ${
            isActive ? 'bg-white text-brand-dark font-semibold shadow-lg' : 'text-brand-light-gray hover:bg-brand-gray'
          }`
        }
      >
        {children}
      </NavLink>
    );

  return (
    <div className="w-full">
      <MainLink>
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-4">
            {!hideIcon && item.icon && <item.icon size={20} />}
            <span className="font-semibold">{item.name}</span>
          </div>
          {hasSubmenu && (isOpen ? <FiChevronUp /> : <FiChevronDown />)}
        </div>
      </MainLink>

      {hasSubmenu && isOpen && (
        <div className="mt-1 mx-2 bg-brand-gray bg-opacity-20 rounded-lg flex flex-col overflow-hidden">
          {item.submenu.map((subItem) => (
            <NavLink
              key={subItem.name}
              to={subItem.path}
              className={({ isActive }) =>
                `px-4 py-3 text-sm transition-colors ${
                  isActive ? 'text-white font-semibold bg-brand-gray' : 'text-gray-300 hover:text-white hover:bg-brand-gray hover:bg-opacity-50'
                }`
              }
            >
              {subItem.name}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://christian-videos-api.onrender.com/category');
        const result = await response.json();
        if (result.success && result.data) {
          const mappedData = result.data.map(item => ({
            name: item.name,
            path: `/categorydetail/${item.id}`,
            submenu: item.subcategories?.map(sub => ({
              name: sub.name,
              path: `/categorydetail/${sub.id}`
            })) || []
          }));
          setCategories(mappedData);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);
  
  const navItems = [
    { name: 'Search', icon: FiSearch, path: '/search' },
    { name: 'Home', icon: FiHome, path: '/' },
    { name: 'Watchlist', icon: FiBookmark, path: '/watchlist' },
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      <button 
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed md:hidden top-4 left-4 z-40 p-2 rounded-lg bg-brand-gray text-white"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 md:hidden z-30"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-screen w-64 bg-brand-dark flex flex-col items-center py-8 z-50 border-r border-gray-800 transition-transform duration-300 md:translate-x-0 ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <NavLink 
          to="/" 
          className="w-full px-4 mb-8"
          onClick={() => setIsMobileOpen(false)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-accent flex items-center justify-center">
              <span className="text-white font-bold text-lg">CV</span>
            </div>
            <span className="text-white font-bold text-lg">Christian Videos</span>
          </div>
        </NavLink>
        
        <nav className="flex flex-col items-start gap-2 w-full px-4">
          {navItems.map((item) => (
            <SidebarMenuItem key={item.name} item={item} />
          ))}
        </nav>

        <div className="w-full px-8 my-8">
          <div className="border-t border-gray-700" />
        </div>

        <nav className="flex flex-col items-start gap-2 w-full px-4 flex-1 overflow-y-auto">
          <h3 className="text-gray-500 font-semibold uppercase text-sm px-4 mb-2 sticky top-0">Categories</h3>
          {categories.map((category) => (
            <SidebarMenuItem key={category.name} item={category} hideIcon={true} />
          ))}
        </nav>

        <div className="mt-auto w-full px-4">
          <SidebarMenuItem item={{ name: 'Sign In / Sign Up', icon: FiUser, path: '/auth' }} />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
